
import { Component } from "vue-property-decorator";
import ClickableTextFieldComponent from "@/components/common/ClickableTextField.vue";
import ConfirmationDialog from "@/components/common/ConfirmationDialog.vue";
import {
  getTimeTrackingConfigurations,
  saveTimeTrackingConfigurations,
} from "@/data/repository/ClientRepository";
import { TimeTrackingConfigurations } from "@/data/models/Client";
import { updateUserSettings } from "@/data/repository/SettingsRepository";
import { UserSettingsModule } from "@/store/modules/UserSettingsModule";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";
import {
  DEFAULT_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
  MAX_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
  MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
} from "@/data/models/TimeTracking";

@Component({
  components: {
    "clickable-text-field": ClickableTextFieldComponent,
    "confirmation-dialog": ConfirmationDialog,
  },
})
export default class TimeTrackingConfigurationsComponent extends SettingsPageMixin {
  timeTrackingConfigurations: TimeTrackingConfigurations | null = null;

  specifyStartEnd = false;
  isStartEndMandatory = false;
  minGeneralGapDurationInMin = 5;
  travelArrivalRadiusInMeters = DEFAULT_TRAVEL_ARRIVAL_RADIUS_IN_METERS;

  /** Exposed for the input's native floor; the range itself is enforced in the validator. */
  minTravelArrivalRadiusInMeters = MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS;

  private initialSpecifyStartEnd = false;
  private initialIsStartEndMandatory = false;
  private initialMinGeneralGapDurationInMin = 5;
  private initialTravelArrivalRadiusInMeters = DEFAULT_TRAVEL_ARRIVAL_RADIUS_IN_METERS;

  get hasChanged(): boolean {
    return (
      this.specifyStartEnd !== this.initialSpecifyStartEnd ||
      this.isStartEndMandatory !== this.initialIsStartEndMandatory ||
      this.minGeneralGapDurationInMin !== this.initialMinGeneralGapDurationInMin ||
      this.travelArrivalRadiusInMeters !== this.initialTravelArrivalRadiusInMeters
    );
  }

  minGeneralGapDurationValidation(input: string) {
    const value = Number(input);
    const isValid = Number.isInteger(value) && value >= 0;
    return isValid ? null : this.$t("settings.errorInvalidNumber");
  }

  onNewMinGeneralGapDurationInMin(input: string) {
    const minGeneralGapDurationInMin = Number(input);
    this.minGeneralGapDurationInMin = Math.max(0, minGeneralGapDurationInMin);
  }

  /**
   * Validates the arrival radius, in metres.
   *
   * A floor of 20 m because below that GPS noise alone would decide which button a crew sees, and a
   * ceiling because "arrived" covering half a village stops meaning anything.
   */
  travelArrivalRadiusValidation(input: string) {
    const value = Number(input);
    const isValid =
      Number.isInteger(value) &&
      value >= MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS &&
      value <= MAX_TRAVEL_ARRIVAL_RADIUS_IN_METERS;
    return isValid
      ? null
      : this.$t("settings.errorNumberRange", [
          MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
          MAX_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
        ]);
  }

  onNewTravelArrivalRadius(input: string) {
    this.travelArrivalRadiusInMeters = Math.min(
      MAX_TRAVEL_ARRIVAL_RADIUS_IN_METERS,
      Math.max(MIN_TRAVEL_ARRIVAL_RADIUS_IN_METERS, Number(input))
    );
  }

  async mounted() {
    this.setToolbarTitle(this.$t("settings.timeTracking.configuration").toString());
    await this.loadTimeTrackingConfigurations();
  }

  async doSave() {
    await saveTimeTrackingConfigurations({
      specifyStartEnd: this.specifyStartEnd,
      isStartEndMandatory: this.isStartEndMandatory,
    });

    // Persisted as a user setting (unchanged data model); consumed via
    // getMinGeneralGapDurationInMin(). Merge update leaves other fields intact.
    await updateUserSettings({
      minGeneralGapDurationInMin: Math.max(0, this.minGeneralGapDurationInMin),
      // Read by IRES Work's travel planner; nothing in Office consumes it.
      travelArrivalRadiusInMeters: this.travelArrivalRadiusInMeters,
    });
  }

  async loadTimeTrackingConfigurations(){
    this.timeTrackingConfigurations = await getTimeTrackingConfigurations();

    this.specifyStartEnd = this.timeTrackingConfigurations?.specifyStartEnd ?? false;
    this.isStartEndMandatory = this.timeTrackingConfigurations?.isStartEndMandatory ?? false;
    this.minGeneralGapDurationInMin =
      Math.max(0, UserSettingsModule.userSettings.minGeneralGapDurationInMin ?? 5);

    this.initialSpecifyStartEnd = this.specifyStartEnd;
    this.initialIsStartEndMandatory = this.isStartEndMandatory;
    this.travelArrivalRadiusInMeters =
      UserSettingsModule.userSettings.travelArrivalRadiusInMeters
      ?? DEFAULT_TRAVEL_ARRIVAL_RADIUS_IN_METERS;

    this.initialMinGeneralGapDurationInMin = this.minGeneralGapDurationInMin;
    this.initialTravelArrivalRadiusInMeters = this.travelArrivalRadiusInMeters;
  }
}
